import * as THREE from "three/webgpu";

const EVENT_INTERVAL = 43;
const EVENT_DURATION = 1.8;

export class RareCelestialEventSystem {
  private readonly root = new THREE.Group();
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.LineBasicMaterial;
  private readonly line: THREE.Line;
  private readonly flareGeometry: THREE.SphereGeometry;
  private readonly flareMaterial: THREE.MeshBasicMaterial;
  private readonly flare: THREE.Mesh;
  private readonly positions = new Float32Array(6);
  private readonly positionAttribute: THREE.BufferAttribute;
  private age = Number.POSITIVE_INFINITY;
  private nextAt = EVENT_INTERVAL;
  private eventIndex = 0;
  private accessibleMode = false;
  private reducedMotion = false;
  private mode: "transient" | "streak" = "streak";

  constructor(scene: THREE.Scene) {
    this.geometry = new THREE.BufferGeometry();
    this.positionAttribute = new THREE.BufferAttribute(this.positions, 3);
    this.positionAttribute.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("position", this.positionAttribute);
    this.material = new THREE.LineBasicMaterial({
      color: 0xdde9ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      toneMapped: false,
    });
    this.line = new THREE.Line(this.geometry, this.material);
    this.line.visible = false;

    this.flareGeometry = new THREE.SphereGeometry(0.36, 16, 16);
    this.flareMaterial = new THREE.MeshBasicMaterial({
      color: 0xf2f5ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      toneMapped: false,
    });
    this.flare = new THREE.Mesh(this.flareGeometry, this.flareMaterial);
    this.flare.visible = false;
    this.root.add(this.line, this.flare);
    scene.add(this.root);
  }

  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
  }
  setReducedMotion(reduced: boolean) {
    this.reducedMotion = reduced;
  }

  update(delta: number, elapsed: number) {
    if (this.reducedMotion || this.accessibleMode) {
      this.line.visible = false;
      this.flare.visible = false;
      return;
    }
    if (elapsed >= this.nextAt && !this.line.visible && !this.flare.visible) this.trigger(elapsed);
    if (!this.line.visible && !this.flare.visible) return;

    this.age += delta;
    const progress = Math.min(this.age / EVENT_DURATION, 1);
    const envelope = Math.sin(progress * Math.PI);
    if (this.mode === "streak") {
      this.material.opacity = envelope * 0.28;
      this.root.position.x += delta * 4.8;
      this.root.position.y += delta * 0.9;
    } else {
      this.flareMaterial.opacity = envelope * 0.38;
      this.flare.scale.setScalar(0.6 + progress * 2.8);
    }

    if (progress >= 1) {
      this.line.visible = false;
      this.flare.visible = false;
      this.eventIndex += 1;
      this.nextAt = elapsed + EVENT_INTERVAL + (this.eventIndex % 3) * 17;
    }
  }

  dispose(scene: THREE.Scene) {
    scene.remove(this.root);
    this.geometry.dispose();
    this.material.dispose();
    this.flareGeometry.dispose();
    this.flareMaterial.dispose();
  }

  private trigger(elapsed: number) {
    const phase = this.eventIndex * 1.73 + elapsed * 0.017;
    const x = -46 + Math.sin(phase) * 18;
    const y = 18 + Math.cos(phase * 0.7) * 14;
    const z = -76 + Math.sin(phase * 0.33) * 14;
    this.mode = this.eventIndex % 3 === 2 ? "transient" : "streak";
    this.root.position.set(0, 0, 0);
    this.age = 0;

    if (this.mode === "streak") {
      this.positions[0] = x;
      this.positions[1] = y;
      this.positions[2] = z;
      this.positions[3] = x - 16;
      this.positions[4] = y + 3.5;
      this.positions[5] = z + 7;
      this.positionAttribute.needsUpdate = true;
      this.material.opacity = 0;
      this.line.visible = true;
      this.flare.visible = false;
    } else {
      this.flare.position.set(x, y, z);
      this.flare.scale.setScalar(0.6);
      this.flareMaterial.opacity = 0;
      this.flare.visible = true;
      this.line.visible = false;
    }
  }
}
